// components/tours/ToursCreateFilters.tsx
import { Check, ChevronsUpDown, Filter, RotateCcw } from "lucide-react";
import type { Country } from "../../api/ToursApi";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/utils";
import { Badge } from "@/shared/components/ui/badge";
import { DatePickerWithPresets } from "@/shared/components/range-picker/RangePicker";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { DEFAULT_FILTERS } from "../../constants/filters";
import type { ToursFiltersState } from "../../types/filters";

interface ToursFiltersProps {
  filters: ToursFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<ToursFiltersState>>;
  countries: Country[];
  onSave: () => void;
  isSaving?: boolean;
}

function CountrySelect({
  label,
  selected,
  countries,
  onChange,
}: {
  label: string;
  selected: number[];
  countries: Country[];
  onChange: (id: number) => void;
}) {
  const selectedLabels = countries
    .filter((c) => selected.includes(c.id))
    .map((c) => c.name);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-10 min-w-[160px] justify-between"
        >
          <span className="truncate max-w-[160px]">
            {selectedLabels.length === 0
              ? label
              : selectedLabels.length === 1
              ? selectedLabels[0]
              : `Выбрано: ${selectedLabels.length}`}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0" align="start">
        <Command>
          <CommandInput placeholder="Поиск страны..." />
          <CommandList>
            <CommandEmpty>Ничего не найдено</CommandEmpty>
            <CommandGroup>
              {countries.map((country) => {
                const isSelected = selected.includes(country.id);
                return (
                  <CommandItem
                    key={country.id}
                    onSelect={() => onChange(country.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {country.name}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        {selectedLabels.length > 0 && (
          <div className="border-t p-2 flex flex-wrap gap-1">
            {selectedLabels.map((label) => (
              <Badge key={label} variant="primary" className="text-xs">
                {label}
              </Badge>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function ToursCreateFilters({
  filters,
  setFilters,
  countries,
  onSave,
  isSaving = false,
}: ToursFiltersProps) {
  // trips хранит [fromId, toId] — выбор страны добавляет/убирает id
  const toggle = (field: "tripsFrom" | "tripsTo") => (id: number) => {
    setFilters((prev) => ({
      ...prev,
      [field]: prev[field].includes(id)
        ? prev[field].filter((x) => x !== id)
        : [...prev[field], id],
    }));
  };

  const handleDateChange = (dateFrom: string, dateTo: string) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo }));
  };

  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  return (
    <div className="bg-white rounded-t-lg border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Filter className="w-5 h-5 text-gray-500 shrink-0" />

        {/* 1. Мультивыбор стран */}
        <CountrySelect
          label="Откуда"
          selected={filters.tripsFrom}
          countries={countries}
          onChange={toggle("tripsFrom")}
        />

        <CountrySelect
          label="Куда"
          selected={filters.tripsTo}
          countries={countries}
          onChange={toggle("tripsTo")}
        />

        {/* 2. Фильтр по датам */}
        <DatePickerWithPresets
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          onChange={handleDateChange}
        />

        {/* 3. Радиокнопки: туда и обратно */}
        <div className="flex items-center gap-2 border rounded-md px-3 h-10">
          <RadioGroup
            value={
              filters.isRoundTrip === null
                ? ""
                : filters.isRoundTrip
                ? "roundtrip"
                : "oneway"
            }
            onValueChange={(val) =>
              setFilters((prev) => ({
                ...prev,
                isRoundTrip: val === "roundtrip",
                isDirect: val === "oneway" ? true : prev.isDirect,
              }))
            }
            className="flex items-center gap-4"
          >
            <div className="flex items-center gap-1.5">
              <RadioGroupItem value="oneway" id="oneway" />
              <Label
                htmlFor="oneway"
                className="text-sm cursor-pointer whitespace-nowrap"
              >
                Только туда
              </Label>
            </div>
            <div className="flex items-center gap-1.5">
              <RadioGroupItem value="roundtrip" id="roundtrip" />
              <Label
                htmlFor="roundtrip"
                className="text-sm cursor-pointer whitespace-nowrap"
              >
                Туда и обратно
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* 4. Диапазон цен */}
        <div className="flex items-center gap-1.5 border rounded-md px-3 h-10">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            Цена:
          </span>
          <Input
            type="number"
            placeholder="от"
            value={filters.priceFrom}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, priceFrom: e.target.value }))
            }
            className="border-0 shadow-none w-20 h-8 p-0 text-sm focus-visible:ring-0"
          />
          <span className="text-muted-foreground">—</span>
          <Input
            type="number"
            placeholder="до"
            value={filters.priceTo}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, priceTo: e.target.value }))
            }
            className="border-0 shadow-none w-20 h-8 p-0 text-sm focus-visible:ring-0"
          />
        </div>

        <Button
          size="sm"
          className="h-10"
          onClick={onSave}
          disabled={isSaving}
        >
          {isSaving ? "Сохранение..." : "Сохранить фильтр"}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          className="h-10 gap-1.5"
          onClick={resetFilters}
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Сброс
        </Button>
      </div>
    </div>
  );
}
